import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import GenericInput from "src/components/input-components/GenericTextInput";
import GenericTextAreaInput from "src/components/input-components/GenericTextAreaInput";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ImagePicker from "src/components/input-components/GenericImagePicker";
import { IWidgetType, WidgetType } from "src/PageWidgetSection";

// --- Type Definitions ---
export interface IServicePageItem {
  Title: string;
  ImageUrl: string;
  Description: string;
  Link: string;
  LinkTitle: string;
}

export interface IServicePageFilter {
  Title: string;
  Link: string;
}

export interface IServicePageWidget extends IWidgetType {
  type: WidgetType.ServicePage; // literal type for service page
  Title: string;
  Items: IServicePageItem[];
  Filters: IServicePageFilter[];
}

// --- Props for the ServicePageItem component ---
interface ServicePageItemProps {
  item: IServicePageWidget;
  error: any;
  onChange: (updatedItem: IServicePageWidget, field: string) => void;
  onblur: (field: string, index?: number) => void;
}

const ServicePageItem: React.FC<ServicePageItemProps> = ({
  item,
  error,
  onChange,
  onblur,
}) => {
  // Overall widget fields.
  const widgetTitle: string = item.Title || "";
  const serviceItems: IServicePageItem[] = Array.isArray(item.Items) ? item.Items : [];
  const filters: IServicePageFilter[] = Array.isArray(item.Filters) ? item.Filters : [];

  // Local state for image picker (for service item images)
  const [imagePickerOpen, setImagePickerOpen] = useState<boolean>(false);
  const [currentImageIndex, setCurrentImageIndex] = useState<number | null>(null);

  // Helper: if value is an event, extract its target.value; otherwise, use it directly.
  const getValue = (
    e: React.ChangeEvent<HTMLInputElement> | string
  ): string => (typeof e === "string" ? e : e.target.value);

  // --- Overall Title Handler ---
  const handleTitleChange = (
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    onChange({ ...item, Title: getValue(e) }, "Title");
  };

  // --- Service Items Handlers ---
  const handleServiceItemChange = (
    index: number,
    field: keyof IServicePageItem,
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    const newValue = getValue(e);
    const updatedItems = [...serviceItems];
    updatedItems[index] = { ...updatedItems[index], [field]: newValue };
    onChange({ ...item, Items: updatedItems }, "Items");
  };

  const handleAddServiceItem = () => {
    const newItem: IServicePageItem = {
      Title: "",
      ImageUrl: "",
      Description: "",
      Link: "",
      LinkTitle: "",
    };
    const updatedItems = [...serviceItems, newItem];
    onChange({ ...item, Items: updatedItems }, "Items");
  };

  const handleRemoveServiceItem = (index: number) => {
    const updatedItems = serviceItems.filter((_, i) => i !== index);
    onChange({ ...item, Items: updatedItems }, "Items");
  };

  // --- Image Picker Handlers for Service Items ---
  const handleOpenImagePicker = (index: number) => {
    setCurrentImageIndex(index);
    setImagePickerOpen(true);
  };

  const handleImageSelect = (selected: string | string[]) => {
    if (currentImageIndex !== null) {
      const newImage = Array.isArray(selected) ? selected[0] : selected;
      const updatedItems = [...serviceItems];
      updatedItems[currentImageIndex] = {
        ...updatedItems[currentImageIndex],
        ImageUrl: newImage,
      };
      onChange({ ...item, Items: updatedItems }, "Items");
    }
    setImagePickerOpen(false);
    setCurrentImageIndex(null);
  };

  // --- Filters Handlers ---
  const handleFilterChange = (
    index: number,
    field: keyof IServicePageFilter,
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    const newValue = getValue(e);
    const updatedFilters = [...filters];
    updatedFilters[index] = { ...updatedFilters[index], [field]: newValue };
    onChange({ ...item, Filters: updatedFilters }, "Filters");
  };

  const handleAddFilter = () => {
    const newFilter: IServicePageFilter = { Title: "", Link: "" };
    const updatedFilters = [...filters, newFilter];
    onChange({ ...item, Filters: updatedFilters }, "Filters");
  };

  const handleRemoveFilter = (index: number) => {
    const updatedFilters = filters.filter((_, i) => i !== index);
    onChange({ ...item, Filters: updatedFilters }, "Filters");
  };

  return (
    <Card elevation={2} sx={{ p: 2, mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Service Page Widget
        </Typography>
        {/* Overall Widget Title */}
        <GenericInput
          type="text"
          name="Title"
          title="Widget Title"
          value={widgetTitle}
          error={error?.Title || ""}
          onChange={(e) => handleTitleChange(e)}
          onBlur={() => onblur("Title")}
          disabled={false}
        />

        {/* Service Items Section */}
        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          Service Items
        </Typography>
        {serviceItems.map((service, index) => (
          <Box
            key={index}
            sx={{
              border: "1px solid #ddd",
              p: 2,
              borderRadius: "4px",
              mb: 2,
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <GenericInput
                  type="text"
                  name={`ServiceTitle-${index}`}
                  title="Service Title"
                  value={service.Title}
                  error={error?.[index]?.Title || ""}
                  onChange={(e) => handleServiceItemChange(index, "Title", e)}
                  onBlur={() => onblur("Title", index)}
                  disabled={false}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    height: 100,
                    width: 150,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    backgroundColor: "#f9f9f9",
                  }}
                  onClick={() => handleOpenImagePicker(index)}
                >
                  {service.ImageUrl ? (
                    <img
                      src={service.ImageUrl}
                      alt={`Service ${index + 1}`}
                      style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                    />
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Select Image
                    </Typography>
                  )}
                </Box>
                {error?.[index]?.ImageUrl && (
                  <Typography variant="body2" color="error">
                    {error?.[index]?.ImageUrl}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                <GenericTextAreaInput
                  name={`ServiceDescription-${index}`}
                  title="Service Description"
                  value={service.Description}
                  error={error?.[index]?.Description || ""}
                  onChange={(e) => handleServiceItemChange(index, "Description", e)}
                  onBlur={() => onblur("Description", index)}
                  disabled={false}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <GenericInput
                  type="text"
                  name={`LinkTitle-${index}`}
                  title="Link Title"
                  value={service.LinkTitle}
                  error={error?.[index]?.LinkTitle || ""}
                  onChange={(e) => handleServiceItemChange(index, "LinkTitle", e)}
                  onBlur={() => onblur("LinkTitle", index)}
                  disabled={false}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <GenericInput
                  type="text"
                  name={`Link-${index}`}
                  title="Link"
                  value={service.Link}
                  error={error?.[index]?.Link || ""}
                  onChange={(e) => handleServiceItemChange(index, "Link", e)}
                  onBlur={() => onblur("Link", index)}
                  disabled={false}
                />
              </Grid>
              <Grid item xs={12} display="flex" justifyContent="flex-end">
                <IconButton
                  color="error"
                  onClick={() => handleRemoveServiceItem(index)}
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        ))}
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddServiceItem}>
          Add Service Item
        </Button>

        {/* Filters Section */}
        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          Filters
        </Typography>
        {filters.map((filter, index) => (
          <Box
            key={index}
            sx={{
              border: "1px solid #ddd",
              p: 2,
              borderRadius: "4px",
              mb: 2,
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={5}>
                <GenericInput
                  type="text"
                  name={`FilterTitle-${index}`}
                  title="Filter Title"
                  value={filter.Title}
                  error={error?.Filters?.[index]?.Title || ""}
                  onChange={(e) => handleFilterChange(index, "Title", e)}
                  onBlur={() => onblur("Title", index)}
                  disabled={false}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <GenericInput
                  type="text"
                  name={`Filter-${index}`}
                  title="Filter Link"
                  value={filter.Link}
                  error={error?.Filters?.[index]?.Link || ""}
                  onChange={(e) => handleFilterChange(index, "Link", e)}
                  onBlur={() => onblur("Link", index)}
                  disabled={false}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <IconButton color="error" onClick={() => handleRemoveFilter(index)}>
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        ))}
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddFilter}>
          Add Filter
        </Button>
      </CardContent>
      {imagePickerOpen && (
        <ImagePicker
          open={imagePickerOpen}
          multiSelect={false}
          onClose={() => setImagePickerOpen(false)}
          onSelect={handleImageSelect}
        />
      )}
    </Card>
  );
};

export default ServicePageItem;
